import { supabase } from '@/integrations/supabase/client';

export interface Comment {
  id: string;
  content: string;
  user_id: string;
  interview_id: string;
  parent_id?: string;
  comment_type: 'comment' | 'discussion' | 'feedback';
  created_at: string;
  updated_at: string;
  profiles?: {
    display_name: string;
    avatar_url?: string;
  };
  replies?: Comment[];
}

export interface CreateCommentData {
  content: string;
  interview_id: string;
  parent_id?: string;
  comment_type?: 'comment' | 'discussion' | 'feedback';
}

export class CommentsService {
  static async getCommentsForInterview(interviewId: string): Promise<Comment[]> {
    const { data, error } = await supabase
      .from('comments')
      .select(`
        *,
        profiles (
          display_name,
          avatar_url
        )
      `)
      .eq('interview_id', interviewId)
      .is('parent_id', null)
      .order('created_at', { ascending: true });

    if (error) {
      console.error('Error fetching comments:', error);
      throw error;
    }

    // Fetch replies for each comment
    const commentsWithReplies = await Promise.all(
      (data || []).map(async (comment) => {
        const replies = await this.getRepliesForComment(comment.id);
        return { ...comment, replies } as Comment;
      })
    );

    return commentsWithReplies;
  }

  static async getRepliesForComment(commentId: string): Promise<Comment[]> {
    const { data, error } = await supabase
      .from('comments')
      .select(`
        *,
        profiles (
          display_name,
          avatar_url
        )
      `)
      .eq('parent_id', commentId)
      .order('created_at', { ascending: true });

    if (error) {
      console.error('Error fetching replies:', error);
      return [];
    }

    return (data || []) as Comment[];
  }

  static async createComment(commentData: CreateCommentData): Promise<Comment | null> {
    const { data: { user } } = await supabase.auth.getUser();
    
    if (!user) {
      throw new Error('User must be authenticated to create comments');
    }

    const { data, error } = await supabase
      .from('comments')
      .insert({
        content: commentData.content,
        interview_id: commentData.interview_id,
        parent_id: commentData.parent_id,
        comment_type: commentData.comment_type || 'comment',
        user_id: user.id,
      })
      .select(`
        *,
        profiles (
          display_name,
          avatar_url
        )
      `)
      .single();

    if (error) {
      console.error('Error creating comment:', error);
      throw error;
    }

    return data as Comment;
  }

  static async updateComment(commentId: string, content: string): Promise<Comment | null> {
    const { data, error } = await supabase
      .from('comments')
      .update({ content, updated_at: new Date().toISOString() })
      .eq('id', commentId)
      .select(`
        *,
        profiles (
          display_name,
          avatar_url
        )
      `)
      .single();

    if (error) {
      console.error('Error updating comment:', error);
      throw error;
    }

    return data as Comment;
  }

  static async deleteComment(commentId: string): Promise<boolean> {
    const { error } = await supabase
      .from('comments')
      .delete()
      .eq('id', commentId);

    if (error) {
      console.error('Error deleting comment:', error);
      throw error;
    }

    return true;
  }

  static async getCommentStats(interviewId: string): Promise<{
    totalComments: number;
    commentTypes: Record<string, number>;
  }> {
    const { data, error } = await supabase
      .from('comments')
      .select('comment_type')
      .eq('interview_id', interviewId);

    if (error) {
      console.error('Error fetching comment stats:', error);
      return { totalComments: 0, commentTypes: {} };
    }

    const commentTypes = data.reduce((acc, comment) => {
      acc[comment.comment_type] = (acc[comment.comment_type] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    return {
      totalComments: data.length,
      commentTypes,
    };
  }
}